/**
 * FirmwareUploadProgress - Progress bar component for firmware uploads
 *
 * Features:
 * - Progress bar (0-100%)
 * - Percentage text display
 * - Status text ("Uploading...", "Verifying...", "Complete")
 * - Animated progress indicator
 */

import { AlertCircle, CheckCircle, Loader2, Upload } from 'lucide-react';
import { cn } from '@/lib/utils';

export type FirmwareUploadStatus = 'idle' | 'uploading' | 'verifying' | 'complete' | 'error';

export interface FirmwareUploadProgressProps {
  /** Current progress percentage (0-100) */
  progress: number;
  /** Current upload status */
  status: FirmwareUploadStatus;
  /** Error message when status is 'error' */
  errorMessage?: string;
  /** File name being uploaded */
  fileName?: string;
  /** Additional class names */
  className?: string;
}

const statusConfig = {
  idle: {
    icon: Upload,
    text: 'Aguardando arquivo...',
    color: 'text-muted-foreground',
    bgColor: 'bg-gray-200',
    progressColor: 'bg-gray-400',
  },
  uploading: {
    icon: Loader2,
    text: 'Enviando firmware...',
    color: 'text-orange-600',
    bgColor: 'bg-orange-100',
    progressColor: 'bg-orange-500',
  },
  verifying: {
    icon: Loader2,
    text: 'Verificando integridade...',
    color: 'text-blue-600',
    bgColor: 'bg-blue-100',
    progressColor: 'bg-blue-500',
  },
  complete: {
    icon: CheckCircle,
    text: 'Upload concluído!',
    color: 'text-green-600',
    bgColor: 'bg-green-100',
    progressColor: 'bg-green-500',
  },
  error: {
    icon: AlertCircle,
    text: 'Erro no upload',
    color: 'text-red-600',
    bgColor: 'bg-red-100',
    progressColor: 'bg-red-500',
  },
};

export function FirmwareUploadProgress({
  progress,
  status,
  errorMessage,
  fileName,
  className,
}: FirmwareUploadProgressProps) {
  const config = statusConfig[status];
  const Icon = config.icon;
  const isAnimated = status === 'uploading' || status === 'verifying';

  return (
    <div className={cn('rounded-lg border p-4', className)}>
      {/* Header with icon and status */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Icon className={cn('h-5 w-5', config.color, isAnimated && 'animate-spin')} />
          <span className={cn('text-sm font-medium', config.color)}>
            {status === 'error' && errorMessage ? errorMessage : config.text}
          </span>
        </div>
        <span className={cn('text-sm font-bold', config.color)}>{progress}%</span>
      </div>

      {/* File name */}
      {fileName && (
        <p className="text-xs text-muted-foreground mb-2 truncate">Arquivo: {fileName}</p>
      )}

      {/* Progress bar */}
      <div className={cn('h-3 rounded-full overflow-hidden', config.bgColor)}>
        <div
          className={cn(
            'h-full rounded-full transition-all duration-300 ease-out',
            config.progressColor,
          )}
          style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
        />
      </div>

      {/* Progress details */}
      {status === 'uploading' && (
        <p className="text-xs text-muted-foreground mt-2">
          {progress < 50
            ? 'Preparando dados...'
            : progress < 90
              ? 'Transferindo para o dispositivo...'
              : 'Finalizando transferência...'}
        </p>
      )}
    </div>
  );
}

export default FirmwareUploadProgress;
